import { Box, Button, Grid, Paper, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';
import { NavLink as RouterLink, NavLink } from 'react-router-dom';
import { firstValueFrom, tap } from 'rxjs';

import { If } from 'app/components/if';
import { RequireRole } from 'app/components/require-role';
import { TabPanel } from 'app/components/tab-panel';
import { GeneralConstants } from 'app/constants/general-constants';
import { Role } from 'app/constants/role';
import { SeamStatus } from 'app/constants/seam-status';
import { Store } from 'app/model/cris/store';
import { Partner } from 'app/model/mcp/partner';
import { makeBlankSeamRequest, SeamRequest } from 'app/model/seam-request';
import { dialogService } from 'app/services/dialog.service';
import { messagingService } from 'app/services/messaging.service';
import { partnerService } from 'app/services/partner.service';
import { storeAttributeService } from 'app/services/store/attribute.service';
import { storeService } from 'app/services/store.service';
import { RootState } from 'app/store';
import { StoreDetailView } from 'features/cris/stores/store-detail-view';
import { StoreKeywordGrid } from 'features/cris/stores/store-keyword-grid';

import { AttributeDialog } from './attribute-dialog';
import { AttributeGrid } from './attribute-grid';
import { StoreAbbreviation } from './store-abbreviation';
import { StoreAttributeHistory } from './store-attribute-history';
import { DeployedPatterns } from '../../seam/request/deployed-patterns';
import { selectProdDataStatus } from '../cris.slice';
import { CrisPartnerListGrid } from '../partners/cris-partner-list-grid';

const TAB_PATTERNS = 0;
const TAB_PARTNER_LIST = 1;
const TAB_KEYWORDS = 2;
const TAB_ATTRIBUTES = 3;
const TAB_HISTORY = 4;
const TAB_ABBREVIATION = 5;

interface StoreDetailsProps {
    store: Store;
    onChange: (store: Store) => void;
    showProd?: boolean; // Add showProd prop
}

interface StoreDetailsState {
    tab: number;
    addForm: boolean;
    partners?: Partner[];
}

class StoreDetails extends React.Component<StoreDetailsProps, StoreDetailsState> {
    public constructor(props: Readonly<StoreDetailsProps> | StoreDetailsProps) {
        super(props);

        this.state = {
            tab: 0,
            addForm: false,
        };
    }

    public deployToProd(withKeywords: boolean): void {
        dialogService.showConfirm(
            {
                message: 'Are you sure you want to deploy to production?',
                confirmText: 'Deploy',
            },
            () =>
                storeService.deploy(this.props.store, withKeywords).subscribe(() => {
                    messagingService.showSuccess('Deployed');
                }),
        );
    }

    public render(): React.ReactNode {
        const fakeRequest: SeamRequest = makeBlankSeamRequest({
            id: '0',
            store: this.props.store,
            currentPlace: SeamStatus.REVIEW,
        });

        return (
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <StoreDetailView store={this.props.store} />
                </Grid>
                <Grid item xs={8}>
                    <RequireRole roles={Role.TAXONOMY_ADMIN}>
                        <If condition={!this.props.showProd}>
                            <Paper elevation={3} className='p-2'>
                                <Typography variant='subtitle2'>Store Operations</Typography>
                                <Button
                                    type='button'
                                    variant='outlined'
                                    color='secondary'
                                    className='mt-1 mr-1'
                                    size='small'
                                    component={NavLink}
                                    to={`/cris/stores/${this.props.store.id}/edit`}
                                >
                                    Edit
                                </Button>
                                <Button
                                    type='button'
                                    variant='outlined'
                                    color='error'
                                    size='small'
                                    className='mt-1 mr-1'
                                    component={RouterLink}
                                    to={`/cris/stores/${this.props.store.id}/delete`}
                                >
                                    Delete
                                </Button>
                                <Button
                                    type='button'
                                    variant='contained'
                                    color='success'
                                    className='mt-1 mr-1'
                                    size='small'
                                    onClick={() => this.deployToProd(false)}
                                >
                                    Deploy Prod
                                </Button>
                                <Button
                                    type='button'
                                    variant='contained'
                                    color='info'
                                    className='mt-1'
                                    size='small'
                                    onClick={() => this.deployToProd(true)}
                                >
                                    Deploy with Keywords
                                </Button>
                            </Paper>
                        </If>
                    </RequireRole>
                    <Tabs
                        value={this.state.tab}
                        onChange={(evt, tab) => this.doTabSwitch(tab as number)}
                        indicatorColor='primary'
                        textColor='primary'
                        centered
                    >
                        <Tab label='Patterns' />
                        <Tab label='Partner Mapping' />
                        <Tab label='Keywords' />
                        <Tab label='Attributes' />
                        <Tab label='History' />
                        <Tab label='Abbreviation' />
                    </Tabs>
                    <TabPanel value={this.state.tab} index={TAB_PATTERNS}>
                        <DeployedPatterns request={fakeRequest} />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_PARTNER_LIST}>
                        <CrisPartnerListGrid
                            rows={this.state.partners ?? []}
                            loading={undefined === this.state.partners}
                            paginationModel={{ pageSize: 25, page: 0 }}
                            disableRowSelectionOnClick
                            autoHeight
                            density='compact'
                        />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_KEYWORDS}>
                        <StoreKeywordGrid store={this.props.store} />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_HISTORY}>
                        <StoreAttributeHistory store={this.props.store} />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_ATTRIBUTES}>
                        <Box display='flex'>
                            <If condition={!this.props.showProd}>
                                <Typography variant='body1'>
                                    Edit value/details via table (double click or focus and Enter)
                                </Typography>
                                <Box flexGrow={1} />
                                <Button
                                    variant='outlined'
                                    className='pull-right mb-1'
                                    onClick={() => this.setState({ addForm: true })}
                                >
                                    Add Attribute
                                </Button>
                            </If>
                        </Box>

                        <AttributeDialog
                            open={this.state.addForm}
                            onClose={() => this.setState({ addForm: false })}
                            type='store'
                            onAddAttribute={(attr) => {
                                storeAttributeService.save(attr, this.props.store).subscribe((attribute) => {
                                    this.setState({ addForm: false });
                                    this.props.onChange({
                                        ...this.props.store,
                                        attributes: [...this.props.store.attributes!, attribute],
                                    });
                                });
                            }}
                        />

                        <AttributeGrid
                            rows={this.props.store.attributes ?? []}
                            paginationModel={{ page: 0, pageSize: 25 }}
                            autoHeight
                            density='compact'
                            processRowUpdate={async (newRow, originalRow) => {
                                const attrId = newRow.id;

                                const patch = {
                                    ...(newRow.value !== originalRow.value && { value: newRow.value }),
                                    ...(newRow.details !== originalRow.details && { details: newRow.details }),
                                };

                                const obs = storeAttributeService
                                    .patch({ id: `${attrId}` }, patch, this.props.store)
                                    .pipe(
                                        tap((attribute) => {
                                            this.props.onChange({
                                                ...this.props.store,
                                                attributes: this.props.store.attributes!.map((attr) =>
                                                    attr.id === attribute.id ? attribute : attr,
                                                ),
                                            });
                                        }),
                                    );

                                return firstValueFrom(obs);
                            }}
                        />
                    </TabPanel>
                    <TabPanel value={this.state.tab} index={TAB_ABBREVIATION}>
                        <StoreAbbreviation store={this.props.store} />
                    </TabPanel>
                </Grid>
            </Grid>
        );
    }

    private doTabSwitch(tab: number): void {
        this.setState({ tab }, () => {
            if (undefined !== this.state.partners || tab !== TAB_PARTNER_LIST) {
                return;
            }

            partnerService
                .listAll({
                    pageSize: GeneralConstants.PAGE_SIZE_HUGE,
                    filters: { store: this.props.store.id },
                })
                .subscribe((partners) => {
                    this.setState({ partners });
                });
        });
    }
}

const mapStateToProps = (state: RootState) => ({
    showProd: selectProdDataStatus(state),
});

// ...

const enhanced = connect(mapStateToProps)(StoreDetails);

export { enhanced as StoreDetails };
